/**
 * CPU bake of the smooth terrain fields the terrain shader samples.
 *
 * Per-hex data (terrain class, elevation, moisture) is interpolated barycentrically across the
 * triangular lattice formed by hex centres — so a lone land hex still becomes ~75 % of a hex of
 * land, and every boundary passes through the hex edges — then Gaussian-smoothed per field, so
 * the GPU can draw organic coastlines, contour lines and forest edges that are never hex-shaped.
 *
 * Outputs (all covering the neatline rect, `texel` world units per texel):
 *   A  rgba16f  elevation 0..1 · coast signed distance (world units, + = land) · ∇elev (x, y)
 *   B  rgba8    forest · hills · mountains · marsh weights
 *   C  rgba8    moisture · urban · lake · shallows
 */
import { type GameMap, HEX_SIZE, HEX_W, type HexGrid, isWater, SQRT3, Terrain } from '@friction/engine';
import { toHalf } from '../gfx/gl';
import type { Rect } from '../sheet';

export interface TerrainFields {
  originX: number;
  originY: number;
  texel: number;
  width: number;
  height: number;
  a: Uint16Array;
  b: Uint8Array;
  c: Uint8Array;
  /** Coast signed distance in world units (+ inland), for CPU-side glyph placement. */
  sdf: Float32Array;
  /** Terrain weights (forest, hills, mountains, marsh) × texel, for glyph placement. */
  weights: Float32Array;
}

export const TEXEL = 8;

/** Channels interpolated from per-hex values. */
const Ch = {
  Land: 0,
  Elev: 1,
  Moist: 2,
  Forest: 3,
  Hills: 4,
  Mount: 5,
  Marsh: 6,
  Urban: 7,
  Lake: 8,
  Shallows: 9,
  Count: 10,
} as const;

/** Smoothing radius (world units) per channel. */
const SIGMA: Record<number, number> = {
  [Ch.Land]: 5,
  [Ch.Elev]: 17,
  [Ch.Moist]: 26,
  [Ch.Forest]: 11,
  [Ch.Hills]: 13,
  [Ch.Mount]: 12,
  [Ch.Marsh]: 11,
  [Ch.Urban]: 6,
  [Ch.Lake]: 8,
  [Ch.Shallows]: 12,
};

export function bakeTerrain(map: GameMap, grid: HexGrid, area: Rect): TerrainFields {
  const texel = TEXEL;
  const originX = area.x0 - texel * 2;
  const originY = area.y0 - texel * 2;
  const width = Math.ceil((area.x1 - area.x0) / texel) + 4;
  const height = Math.ceil((area.y1 - area.y0) / texel) + 4;
  const n = width * height;

  // ---- per-hex source values
  const hexVals = new Float32Array(grid.count * Ch.Count);
  for (let h = 0; h < grid.count; h++) {
    const t = map.terrain[h];
    const o = h * Ch.Count;
    hexVals[o + Ch.Land] = isWater(t) ? 0 : 1;
    hexVals[o + Ch.Elev] = map.elevation[h] / 255;
    hexVals[o + Ch.Moist] = map.moisture[h] / 255;
    hexVals[o + Ch.Forest] = t === Terrain.Forest ? 1 : 0;
    hexVals[o + Ch.Hills] = t === Terrain.Hills ? 1 : 0;
    hexVals[o + Ch.Mount] = t === Terrain.Mountains ? 1 : 0;
    hexVals[o + Ch.Marsh] = t === Terrain.Marsh ? 1 : 0;
    hexVals[o + Ch.Urban] = t === Terrain.Urban ? 1 : 0;
    hexVals[o + Ch.Lake] = t === Terrain.Lake ? 1 : 0;
    hexVals[o + Ch.Shallows] = t === Terrain.Shallows ? 1 : 0;
  }

  // ---- barycentric interpolation on the hex-centre lattice
  const fields: Float32Array[] = Array.from({ length: Ch.Count }, () => new Float32Array(n));
  const clampHex = (q: number, r: number): number => {
    const col = Math.min(grid.width - 1, Math.max(0, q + (r - (r & 1)) / 2));
    const row = Math.min(grid.height - 1, Math.max(0, r));
    return row * grid.width + col;
  };
  for (let y = 0; y < height; y++) {
    const wy = originY + (y + 0.5) * texel - HEX_SIZE;
    for (let x = 0; x < width; x++) {
      const wx = originX + (x + 0.5) * texel - HEX_W / 2;
      const q = ((SQRT3 / 3) * wx - wy / 3) / HEX_SIZE;
      const r = ((2 / 3) * wy) / HEX_SIZE;
      const q0 = Math.floor(q);
      const r0 = Math.floor(r);
      const fq = q - q0;
      const fr = r - r0;
      let h0: number;
      let w0: number;
      let w1: number;
      let w2: number;
      if (fq + fr < 1) {
        h0 = clampHex(q0, r0);
        w0 = 1 - fq - fr;
        w1 = fq;
        w2 = fr;
      } else {
        h0 = clampHex(q0 + 1, r0 + 1);
        w0 = fq + fr - 1;
        w1 = 1 - fr;
        w2 = 1 - fq;
      }
      const h1 = clampHex(q0 + 1, r0);
      const h2 = clampHex(q0, r0 + 1);
      const i = y * width + x;
      const o0 = h0 * Ch.Count;
      const o1 = h1 * Ch.Count;
      const o2 = h2 * Ch.Count;
      for (let c = 0; c < Ch.Count; c++)
        fields[c][i] = w0 * hexVals[o0 + c] + w1 * hexVals[o1 + c] + w2 * hexVals[o2 + c];
    }
  }

  const tmp = new Float32Array(n);
  for (let c = 0; c < Ch.Count; c++) gaussianBlur(fields[c], width, height, SIGMA[c] / texel, tmp);

  // ---- coast signed distance: exact EDT of the thresholded land field, then smoothed
  const sdf = signedDistance(fields[Ch.Land], width, height, texel);
  gaussianBlur(sdf, width, height, 1.1, tmp);

  // ---- pack
  const a = new Uint16Array(n * 4);
  const b = new Uint8Array(n * 4);
  const c = new Uint8Array(n * 4);
  const weights = new Float32Array(n * 4);
  const elev = fields[Ch.Elev];
  const gScale = HEX_W / (2 * texel);
  const u8 = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const xl = x > 0 ? i - 1 : i;
      const xr = x < width - 1 ? i + 1 : i;
      const yu = y > 0 ? i - width : i;
      const yd = y < height - 1 ? i + width : i;
      const gx = (elev[xr] - elev[xl]) * gScale;
      const gy = (elev[yd] - elev[yu]) * gScale;
      a[i * 4] = toHalf(elev[i]);
      a[i * 4 + 1] = toHalf(sdf[i]);
      a[i * 4 + 2] = toHalf(gx);
      a[i * 4 + 3] = toHalf(gy);
      b[i * 4] = u8(fields[Ch.Forest][i]);
      b[i * 4 + 1] = u8(fields[Ch.Hills][i]);
      b[i * 4 + 2] = u8(fields[Ch.Mount][i]);
      b[i * 4 + 3] = u8(fields[Ch.Marsh][i]);
      c[i * 4] = u8(fields[Ch.Moist][i]);
      c[i * 4 + 1] = u8(fields[Ch.Urban][i]);
      c[i * 4 + 2] = u8(fields[Ch.Lake][i]);
      c[i * 4 + 3] = u8(fields[Ch.Shallows][i]);
      weights[i * 4] = fields[Ch.Forest][i];
      weights[i * 4 + 1] = fields[Ch.Hills][i];
      weights[i * 4 + 2] = fields[Ch.Mount][i];
      weights[i * 4 + 3] = fields[Ch.Marsh][i];
    }
  }
  return { originX, originY, texel, width, height, a, b, c, sdf, weights };
}

/** Bilinear sample of a scalar field (or one channel of an interleaved one) at a world point. */
export function sampleField(
  f: TerrainFields,
  data: Float32Array,
  wx: number,
  wy: number,
  stride = 1,
  channel = 0,
): number {
  const fx = Math.min(f.width - 1.001, Math.max(0, (wx - f.originX) / f.texel - 0.5));
  const fy = Math.min(f.height - 1.001, Math.max(0, (wy - f.originY) / f.texel - 0.5));
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const i = y0 * f.width + x0;
  const v00 = data[i * stride + channel];
  const v10 = data[(i + 1) * stride + channel];
  const v01 = data[(i + f.width) * stride + channel];
  const v11 = data[(i + f.width + 1) * stride + channel];
  return (v00 * (1 - tx) + v10 * tx) * (1 - ty) + (v01 * (1 - tx) + v11 * tx) * ty;
}

/** In-place separable Gaussian blur (clamped edges). `sigma` in texels. */
export function gaussianBlur(f: Float32Array, w: number, h: number, sigma: number, tmp: Float32Array): void {
  if (sigma < 0.3) return;
  const rad = Math.ceil(sigma * 3);
  const k = new Float32Array(rad * 2 + 1);
  let sum = 0;
  for (let i = -rad; i <= rad; i++) sum += k[i + rad] = Math.exp(-(i * i) / (2 * sigma * sigma));
  for (let i = 0; i < k.length; i++) k[i] /= sum;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let acc = 0;
      for (let j = -rad; j <= rad; j++) {
        const xx = x + j < 0 ? 0 : x + j >= w ? w - 1 : x + j;
        acc += f[row + xx] * k[j + rad];
      }
      tmp[row + x] = acc;
    }
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      let acc = 0;
      for (let j = -rad; j <= rad; j++) {
        const yy = y + j < 0 ? 0 : y + j >= h ? h - 1 : y + j;
        acc += tmp[yy * w + x] * k[j + rad];
      }
      f[y * w + x] = acc;
    }
  }
}

/** Signed distance (world units, + inside) to the 0.5 iso-line of `mask`. */
function signedDistance(mask: Float32Array, w: number, h: number, texel: number): Float32Array {
  const n = w * h;
  const INF = 1e20;
  const toLand = new Float64Array(n);
  const toWater = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const land = mask[i] >= 0.5;
    toLand[i] = land ? 0 : INF;
    toWater[i] = land ? INF : 0;
  }
  edt2d(toLand, w, h);
  edt2d(toWater, w, h);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const d = mask[i] >= 0.5 ? Math.sqrt(toWater[i]) - 0.5 : -(Math.sqrt(toLand[i]) - 0.5);
    out[i] = Math.max(-2000, Math.min(2000, d * texel));
  }
  return out;
}

/** Felzenszwalb–Huttenlocher squared Euclidean distance transform, in place. */
function edt2d(g: Float64Array, w: number, h: number): void {
  const len = Math.max(w, h);
  const f = new Float64Array(len);
  const d = new Float64Array(len);
  const v = new Int32Array(len);
  const z = new Float64Array(len + 1);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = g[y * w + x];
    edt1d(f, h, d, v, z);
    for (let y = 0; y < h; y++) g[y * w + x] = d[y];
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = g[y * w + x];
    edt1d(f, w, d, v, z);
    for (let x = 0; x < w; x++) g[y * w + x] = d[x];
  }
}

function edt1d(f: Float64Array, n: number, d: Float64Array, v: Int32Array, z: Float64Array): void {
  let k = 0;
  v[0] = 0;
  z[0] = -Infinity;
  z[1] = Infinity;
  for (let q = 1; q < n; q++) {
    let s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (s <= z[k]) {
      k--;
      s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    }
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = Infinity;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    d[q] = (q - v[k]) * (q - v[k]) + f[v[k]];
  }
}
